import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '@App/components/AuthProvider/auth-provider.context';
import {
  faCopy,
  faEdit,
  faEmptySet,
  faPaste,
} from '@fortawesome/pro-light-svg-icons';
import { useDispatch, useSelector } from 'react-redux';

import Nav from '@App/components/Nav/Nav';
import EditMobilityOffer from '@App/components/EditMobilityOffer/EditMobilityOffer';
import ConfirmationDialog from '@App/components/ConfirmationDialog/ConfirmationDialog';

import { useUserCompanies } from '@services/query.service';
import { useClearMobilityOfferMutation, useCopyMobilityOfferMutation } from '@services/mutation.service';

import AdminSidepanel from './AdminSidepanel/AdminSidepanel';
import AdminActions from './AdminActions/AdminActions';
import AdminLastUpdate from './AdminLastUpdate/AdminLastUpdate';
import AdminUserManagement from './AdminUserManagement/AdminUserManagement';
import MobilityPlanContent from '../MobilityPlan/MobilityPlanContent/MobilityPlanContent';

import { setShowUserManagementModal } from './admin.slice';
import { mobilityOfferExists } from './admin.utils';

import './admin.styl';

function Admin() {
  const [activeCompanyId, setActiveCompanyId] = useState(undefined);
  const [edit, setEdit] = useState(false);
  const [copiedCompanyId, setCopiedCompanyId] = useState(undefined);
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [showPasteConfirmation, setShowPasteConfirmation] = useState(false);

  const { isLoggedIn, loading: loadingAuth, user } = useAuth();
  const showUserManagementModal = useSelector(state => state.admin.showUserManagementModal);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { data: userCompanies, isLoading } = useUserCompanies();

  if (!loadingAuth && !isLoggedIn) {
    navigate('/');
  }

  if (user && !user.groups?.includes('employers')) {
    navigate('/');
  }

  const activeCompany = activeCompanyId
    ? userCompanies?.flatMap(company => company.branches)?.flat()?.find(branch => branch.OIDN === activeCompanyId)
    : userCompanies?.flatMap(company => company.branches)?.flat()?.[0];

  const clearMutation = useClearMobilityOfferMutation(activeCompany?.OND_VESTNR, () => setEdit(false));
  const currentCompanyIsCopied = activeCompany?.OND_VESTNR === copiedCompanyId;
  const copyMutation = useCopyMobilityOfferMutation(activeCompany?.OND_VESTNR, copiedCompanyId, () => setCopiedCompanyId(undefined));

  const hasMobilityOffer = mobilityOfferExists(activeCompany?.mobilityOffer);

  return (
    <div className="admin">
      <Nav />
      <div className="admin__body">
        <AdminSidepanel
          companies={userCompanies}
          activeCompanyId={activeCompany?.OIDN}
          setActiveCompanyId={(company) => {
            setEdit(false);
            setActiveCompanyId(company);
            window.gtag?.('event', 'admin_click_company_branch_card', {
              event_category: 'Admin',
              event_label: 'Admin click company branch card',
              active_company_branch: company,
            });
          }}
          isLoading={!user || isLoading}
          copiedCompanyId={copiedCompanyId}
        />
        {activeCompany ? (
          <div className="admin__body__content">
            {edit ? (
              <EditMobilityOffer
                companyId={activeCompany.OND_VESTNR}
                onCancel={() => setEdit(false)}
                isAdmin
              />
            ) : (
              <>
                <div className="admin__body__content__info">
                  <p>
                    Hier kan je het&nbsp;
                    <strong>mobiliteitsaanbod van je onderneming aanpassen</strong>
                    . Elke vestiging heeft zijn eigen aanbod.
                    Kies een vestiging uit de lijst links en klik vervolgens op de knop &quot;Aanpassen&quot; om het aanbod voor elke&nbsp;
                    vestiging individueel in te vullen of aan te passen met behulp van een eenvoudig formulier.
                  </p>
                  <p>
                    Om&nbsp;
                    <strong>opnieuw te beginnen</strong>
                    &nbsp;kies je de knop &quot;Leegmaken&quot;. Wanneer meerdere vestigingen hetzelfde aanbod hebben,
                    kan je de inhoud van het formulier gemakkelijk&nbsp;
                    <strong>kopiëren en plakken</strong>
                    &nbsp;bij een andere vestiging.
                  </p>
                </div>
                <AdminActions
                  actions={[
                    {
                      id: 'edit',
                      text: 'Aanpassen',
                      onClick: () => {
                        setEdit(true);
                        window.gtag?.('event', 'admin_click_edit_mobilty_info', {
                          event_category: 'Admin',
                          event_label: 'Admin click edit mobility info',
                        });
                      },
                      icon: faEdit,
                    },
                    {
                      id: 'empty',
                      text: 'Leegmaken',
                      icon: faEmptySet,
                      onClick: () => {
                        setShowClearConfirmation(true);
                        window.gtag?.('event', 'admin_click_clear_mobilty_info', {
                          event_category: 'Admin',
                          event_label: 'Admin click clear mobility info',
                        });
                      },
                      isLoading: clearMutation.isPending,
                      disabled: !activeCompany?.mobilityOffer,
                    },
                    {
                      id: 'copy',
                      text: currentCompanyIsCopied ? 'Ongedaan maken' : 'Kopiëren',
                      icon: faCopy,
                      disabled: !activeCompany?.mobilityOffer,
                      onClick: () => {
                        setCopiedCompanyId(currentCompanyIsCopied ? undefined : activeCompany?.OND_VESTNR);
                        window.gtag?.('event', 'admin_click_copy_mobilty_info', {
                          event_category: 'Admin',
                          event_label: 'Admin click copy mobility info',
                        });
                      },
                    },
                    {
                      id: 'paste',
                      text: 'Plakken',
                      icon: faPaste,
                      disabled: !copiedCompanyId || currentCompanyIsCopied,
                      onClick: () => {
                        setShowPasteConfirmation(true);
                        window.gtag?.('event', 'admin_click_paste_mobilty_info', {
                          event_category: 'Admin',
                          event_label: 'Admin click paste mobility info',
                        });
                      },
                      isLoading: copyMutation.isPending,
                    },
                  ]}
                />
                {activeCompany?.mobilityOffer && (
                  <AdminLastUpdate
                    updatedAt={activeCompany?.mobilityOffer?.updatedAt}
                    updatedBy={activeCompany?.mobilityOffer?.updatedBy}
                    verified={activeCompany?.mobilityOffer?.verified}
                    isAdmin
                  />
                )}
                {hasMobilityOffer ? (
                  <MobilityPlanContent mobilityOffer={activeCompany?.mobilityOffer} />
                ) : (
                  <div style={{ marginTop: '16px' }}>
                    Er werden nog geen gegevens toegevoegd voor deze vestiging
                  </div>
                )}
              </>
            )}
          </div>
        ) : (
          null
        )}
      </div>
      {showClearConfirmation && (
        <ConfirmationDialog
          title="Leegmaken"
          content={(
            <div>
              <p>
                Weet je zeker dat je het volledige mobiliteitsplan wilt leegmaken?
              </p>
              <p>
                Dit kan niet ongedaan gemaakt worden.
              </p>
            </div>
          )}
          onClose={() => setShowClearConfirmation(false)}
          onConfirm={() => {
            setShowClearConfirmation(false);
            clearMutation.mutate();
          }}
        />
      )}
      {showPasteConfirmation && (
        <ConfirmationDialog
          title="Plakken"
          content={(
            <div>
              <p>
                Weet je zeker dat je het volledige mobiliteitsplan wilt plakken?
              </p>
              <p>
                Dit kan niet ongedaan gemaakt worden.
              </p>
            </div>
          )}
          onClose={() => setShowPasteConfirmation(false)}
          onConfirm={() => {
            setShowPasteConfirmation(false);
            copyMutation.mutate();
          }}
        />
      )}
      {showUserManagementModal && (
        <AdminUserManagement
          onClose={() => dispatch(setShowUserManagementModal(false))}
        />
      )}
    </div>
  )
}

export default Admin;